import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { I18nService, type I18nService as I18nServiceType } from '@telecom/shared/services/i18n';
import { RavenService, type RavenService as RavenServiceType } from '@telecom/shared/services/raven-service/raven-service';
import type { Config, DriveStep, Driver, PopoverDOM } from 'driver.js';
import { TRACK_EVENT_TYPE } from '@/constants';
import {
    TRACK_EVENT_AGENT_GUIDE_WELCOME_CTA_CLICKED,
    TRACK_EVENT_AGENT_GUIDE_WELCOME_SHOWN,
    TRACK_EVENT_AGENT_GUIDE_AGENT_SWITCHED,
    TRACK_EVENT_AGENT_GUIDE_STEP1_SPOTLIGHTED,
} from '@/constants/track-events';
import {
    OnboardingBrowserRuntime,
    type OnboardingBrowserRuntime as OnboardingBrowserRuntimeType,
} from '../../common/services/onboarding-browser-runtime';
import {
    OnboardingEligibilityService,
    type OnboardingEligibilityService as OnboardingEligibilityServiceType,
} from '../../common/services/onboarding-eligibility-service';
import { registerOnboardingLocaleMessages } from '../../common/utils/onboarding-locale';
import { messages } from '../locales';
import type {
    HomeV2EntryOnboardingService,
    HomeV2EntryOnboardingStartOptions,
    HomeV2OnboardingAgent,
} from '../home-v2-entry-onboarding-service';
import {
    HomeV2WelcomePresenter,
    type HomeV2WelcomeHandle,
    type HomeV2WelcomePresenter as HomeV2WelcomePresenterType,
} from '../home-v2-welcome-presenter';
import {
    NewUserInitializationService,
    type NewUserInitializationService as NewUserInitializationServiceType,
} from '@/services/new-user-initialization-service/new-user-initialization-service';

/** Home V2 三个 Agent Tab 的稳定选择器。 */
const AGENT_TABS_SELECTOR = '[data-testid="home-v2-agent-tabs"]';
/** Step 1 无按钮气泡的样式类。 */
const AGENT_GUIDE_POPOVER_CLASS = 'home-v2-agent-guide-popover';
/** Step 1 成功激活后的展示时长。 */
const AGENT_GUIDE_DURATION_MS = 4000;
/** Welcome 成功挂载后的展示时长。 */
const WELCOME_DURATION_MS = 3000;
/** Welcome 完成退场后到 Step 1 开始前的缓冲时长。 */
const STEP_ONE_TRANSITION_DELAY_MS = 2000;

/** Step 1 结束后等待输入引导的共享结果。 */
interface SequenceDeferred {
    /** 当前共享 Promise。 */
    promise: Promise<boolean>;
    /** 完成当前共享 Promise。 */
    resolve: (canContinue: boolean) => void;
}

/** 移除 Driver 默认按钮，只保留设计稿中的说明文案。 */
function removePopoverControls(popover: PopoverDOM): void {
    popover.closeButton.remove();
    popover.footer.remove();
}

/** Home V2 Agent Tab 首次引导与后续输入引导的统一编排器。 */
@injectable()
export class HomeV2EntryOnboardingServiceImpl implements HomeV2EntryOnboardingService {
    /** 当前 Welcome 弹窗的生命周期句柄。 */
    private _welcomeHandle?: HomeV2WelcomeHandle;
    /** Welcome 自动结束计时器。 */
    private _welcomeTimerId?: number;
    /** Welcome 与 Step 1 之间的过渡计时器。 */
    private _stepOneTransitionTimerId?: number;
    /** 页面销毁时用于结束待决过渡等待。 */
    private _resolveStepOneTransition?: () => void;
    /** 当前 Step 1 使用的 Driver。 */
    private _driver?: Driver;
    /** 当前页面选中的 Agent。 */
    private _activeAgent?: HomeV2OnboardingAgent;
    /** 当前 Step 1 是否需要说明 URL 中已有问题。 */
    private _hasPrefilledInput = false;
    /** Step 1 启动流程。 */
    private _startPromise?: Promise<boolean>;
    /** Step 1 结束后向 Agent service 广播的结果。 */
    private _sequenceDeferred?: SequenceDeferred;
    /** 自动进入现有输入引导的计时器。 */
    private _autoAdvanceTimerId?: number;
    /** Step 1 是否已经成功激活。 */
    private _isStepOneActive = false;
    /** 当前 Driver 是否因四秒自动完成而销毁。 */
    private _isAutoCompleting = false;
    /** 当前页面生命周期内是否已发放输入引导许可。 */
    private _isInputGuideClaimed = false;
    /** 当前页面生命周期内是否已记录账号级入口已展示。 */
    private _hasMarkedEntrySeen = false;
    /** 用于使销毁前的异步启动流程失效。 */
    private _lifecycleId = 0;

    // eslint-disable-next-line max-params
    constructor(
        /** 提供 Driver、DOM、渲染等待和计时器能力。 */
        @inject(OnboardingBrowserRuntime)
        private readonly _runtime: OnboardingBrowserRuntimeType,
        /** 统一处理账号、受众和历史任务资格。 */
        @inject(OnboardingEligibilityService)
        private readonly _onboardingEligibilityService: OnboardingEligibilityServiceType,
        /** 提供当前语言和动态语言包注册能力。 */
        @inject(I18nService)
        private readonly _i18nService: I18nServiceType,
        /** 上报 Welcome、Step 1 与引导内 Agent 切换。 */
        @inject(RavenService)
        private readonly _ravenService: RavenServiceType,
        /** 将 Welcome 弹窗挂载到页面顶层。 */
        @inject(HomeV2WelcomePresenter)
        private readonly _welcomePresenter: HomeV2WelcomePresenterType,
        /** 确保产品准入、积分检查及成功 Toast 已按顺序完成。 */
        @inject(NewUserInitializationService)
        private readonly _newUserInitializationService: NewUserInitializationServiceType,
    ) {}

    /** 同步保存 URL 预填状态，供可能由子组件先触发的 Step 1 使用。 */
    setEntryContext(options: HomeV2EntryOnboardingStartOptions): void {
        this._hasPrefilledInput = options.hasPrefilledInput;
    }

    /** 幂等启动当前 Agent 的 Step 1。 */
    start(
        agent: HomeV2OnboardingAgent,
        options?: HomeV2EntryOnboardingStartOptions,
    ): Promise<boolean> {
        this._activeAgent = agent;
        if (options) this.setEntryContext(options);
        if (this._startPromise) return this._startPromise;

        const lifecycleId = ++this._lifecycleId;
        this._sequenceDeferred = this._createSequenceDeferred();
        this._startPromise = this._start(agent, lifecycleId);
        return this._startPromise;
    }

    /** 等待 Step 1 结束，并只向最终 Agent 发放一次输入引导许可。 */
    async acquireInputGuide(agent: HomeV2OnboardingAgent): Promise<boolean> {
        if (!this._startPromise) void this.start(agent);
        const sequenceDeferred = this._sequenceDeferred;
        if (!sequenceDeferred) return false;

        const canContinue = await sequenceDeferred.promise;
        if (!canContinue || this._activeAgent !== agent || this._isInputGuideClaimed) return false;

        this._isInputGuideClaimed = true;
        return true;
    }

    /** 切换 Agent 时刷新当前文案，并仅在 Step 1 激活期间记录真实切换。 */
    notifyAgentSwitched(fromAgent: HomeV2OnboardingAgent, toAgent: HomeV2OnboardingAgent): void {
        if (fromAgent === toAgent) return;
        this._activeAgent = toAgent;
        if (!this._isStepOneActive || !this._driver?.isActive()) return;

        try {
            this._driver.highlight(this._createStep(toAgent));
        } catch (error) {
            this._runtime.warn('Failed to update Home V2 Agent onboarding:', error);
        }
        this._track(TRACK_EVENT_AGENT_GUIDE_AGENT_SWITCHED, {
            fromAgent,
            toAgent,
        });
    }

    /** 清理 Welcome、Driver、计时器和当前页面的待决输入引导。 */
    destroy(): void {
        const lifecycleId = this._lifecycleId;
        this._disposeWelcome();
        this._clearStepOneTransitionTimer();
        this._clearAutoAdvanceTimer();
        this._isStepOneActive = false;
        this._isAutoCompleting = false;
        this._settleSequence(false, lifecycleId);
        this._lifecycleId += 1;

        const driverInstance = this._driver;
        this._driver = undefined;
        if (driverInstance?.isActive()) driverInstance.destroy();

        this._startPromise = undefined;
        this._sequenceDeferred = undefined;
        this._activeAgent = undefined;
        this._hasPrefilledInput = false;
        this._isInputGuideClaimed = false;
        this._hasMarkedEntrySeen = false;
    }

    /** 校验一次账号资格，依次展示 Welcome 与 Step 1。 */
    // eslint-disable-next-line complexity
    private async _start(agent: HomeV2OnboardingAgent, lifecycleId: number): Promise<boolean> {
        try {
            const isInitializationReady = await this._waitForNewUserInitialization();
            if (!this._isCurrent(lifecycleId)) return false;
            if (!isInitializationReady) {
                this._settleSequence(false, lifecycleId);
                return false;
            }

            const isEligible = await this._onboardingEligibilityService.isEligible();
            if (!this._isCurrent(lifecycleId)) return false;
            if (!isEligible) {
                this._settleSequence(false, lifecycleId);
                return false;
            }

            registerOnboardingLocaleMessages(this._i18nService, messages);
            const wasWelcomeShown = await this._showWelcome(lifecycleId);
            if (!this._isCurrent(lifecycleId)) return false;
            if (wasWelcomeShown) await this._waitForStepOneTransition();
            if (!this._isCurrent(lifecycleId)) return false;
            return await this._startStepOne(agent, lifecycleId);
        } catch (error) {
            if (this._isCurrent(lifecycleId)) {
                this._clearFailedActivation();
                this._settleSequence(true, lifecycleId);
            }
            this._runtime.warn('Failed to start Home V2 Agent onboarding:', error);
            return false;
        }
    }

    /** 等待应用级新用户初始化；异常时终止整段首次引导。 */
    private async _waitForNewUserInitialization(): Promise<boolean> {
        try {
            return await this._newUserInitializationService.waitForEntryOnboarding();
        }
        catch (error) {
            this._runtime.warn('Failed to wait for new user initialization:', error);
            return false;
        }
    }

    /** 展示 Welcome；挂载失败时直接进入 Step 1。 */
    private async _showWelcome(lifecycleId: number): Promise<boolean> {
        let handle: HomeV2WelcomeHandle;
        try {
            handle = this._welcomePresenter.show({
                title: this._i18nService.t('onboarding.homeV2.welcome.title'),
                description: this._i18nService.t('onboarding.homeV2.welcome.description'),
                cta: this._i18nService.t('onboarding.homeV2.welcome.cta'),
                benefit: this._i18nService.t('onboarding.homeV2.welcome.benefit'),
            });
        } catch (error) {
            this._runtime.warn('Failed to show Home V2 Welcome onboarding:', error);
            return false;
        }

        if (!this._isCurrent(lifecycleId)) {
            handle.dispose();
            return false;
        }
        this._welcomeHandle = handle;
        this._markEntrySeen();
        this._track(TRACK_EVENT_AGENT_GUIDE_WELCOME_SHOWN);
        this._welcomeTimerId = this._runtime.setTimeout(
            () => handle.close('auto'),
            WELCOME_DURATION_MS,
        );

        const reason = await handle.result;
        if (this._welcomeHandle === handle) {
            this._clearWelcomeTimer();
            this._welcomeHandle = undefined;
            handle.dispose();
        }
        if (!this._isCurrent(lifecycleId)) return false;
        if (reason === 'cta') this._track(TRACK_EVENT_AGENT_GUIDE_WELCOME_CTA_CLICKED);
        return true;
    }

    /** 校验目标并激活 Step 1；失败时保留原有输入引导作为降级路径。 */
    private async _startStepOne(agent: HomeV2OnboardingAgent, lifecycleId: number): Promise<boolean> {
        await this._runtime.waitForRender();
        if (!this._isCurrent(lifecycleId)) return false;
        const target = this._runtime.querySelector(AGENT_TABS_SELECTOR);
        if (!target) {
            this._settleSequence(true, lifecycleId);
            return false;
        }

        const currentAgent = this._activeAgent ?? agent;
        const driverInstance = this._runtime.createDriver(this._createDriverConfig(lifecycleId));
        this._driver = driverInstance;
        driverInstance.highlight(this._createStep(currentAgent));
        if (!driverInstance.isActive()) {
            this._driver = undefined;
            this._settleSequence(true, lifecycleId);
            return false;
        }

        this._isStepOneActive = true;
        this._markEntrySeen();
        this._track(TRACK_EVENT_AGENT_GUIDE_STEP1_SPOTLIGHTED, { activeAgent: currentAgent });
        this._autoAdvanceTimerId = this._runtime.setTimeout(
            () => this._completeStepOne(lifecycleId),
            AGENT_GUIDE_DURATION_MS,
        );
        return true;
    }

    /** 创建允许 Tab 交互、支持 Escape 关闭的 Driver 配置。 */
    private _createDriverConfig(lifecycleId: number): Config {
        return {
            animate: true,
            duration: 300,
            overlayColor: '#0B2447',
            overlayOpacity: 0.56,
            smoothScroll: false,
            allowClose: true,
            allowScroll: false,
            overlayClickBehavior: () => undefined,
            stagePadding: 5,
            stageRadius: 25,
            disableActiveInteraction: false,
            allowKeyboardControl: true,
            onDestroyed: (_element, _step, { driver: destroyedDriver }) => {
                if (this._driver !== destroyedDriver) return;
                this._driver = undefined;
                this._isStepOneActive = false;
                this._clearAutoAdvanceTimer();
                this._settleSequence(this._isAutoCompleting, lifecycleId);
            },
        };
    }

    /** 创建当前 Agent 对应的 Tab 组高亮和右侧无按钮气泡。 */
    private _createStep(agent: HomeV2OnboardingAgent): DriveStep {
        const messageGroup = this._hasPrefilledInput ? 'prefilledAgent' : 'agent';
        return {
            element: AGENT_TABS_SELECTOR,
            disableActiveInteraction: false,
            popover: {
                description: this._i18nService.t(`onboarding.homeV2.${messageGroup}.${agent}`),
                side: 'right',
                align: 'center',
                showButtons: ['close'],
                popoverClass: AGENT_GUIDE_POPOVER_CLASS,
                onPopoverRender: removePopoverControls,
            },
        };
    }

    /** 四秒后主动关闭 Step 1，并允许最终 Agent 继续输入引导。 */
    private _completeStepOne(lifecycleId: number): void {
        if (!this._isCurrent(lifecycleId) || !this._isStepOneActive) return;
        this._isAutoCompleting = true;
        this._clearAutoAdvanceTimer();
        const driverInstance = this._driver;
        this._driver = undefined;
        this._isStepOneActive = false;
        if (driverInstance?.isActive()) driverInstance.destroy();
        this._settleSequence(true, lifecycleId);
        this._isAutoCompleting = false;
    }

    /** Driver 激活异常时清理可能已创建的遮罩，再降级到原输入引导。 */
    private _clearFailedActivation(): void {
        this._clearAutoAdvanceTimer();
        this._isStepOneActive = false;
        const driverInstance = this._driver;
        this._driver = undefined;
        if (driverInstance?.isActive()) driverInstance.destroy();
    }

    /** 创建当前生命周期唯一的共享结果。 */
    private _createSequenceDeferred(): SequenceDeferred {
        let resolve!: (canContinue: boolean) => void;
        const promise = new Promise<boolean>((promiseResolve) => {
            resolve = promiseResolve;
        });
        return { promise, resolve };
    }

    /** 完成当前生命周期的共享结果，重复调用不会改变既有结果。 */
    private _settleSequence(canContinue: boolean, lifecycleId: number): void {
        if (lifecycleId !== this._lifecycleId || !this._sequenceDeferred) return;
        const deferred = this._sequenceDeferred;
        this._sequenceDeferred = {
            promise: deferred.promise,
            resolve: () => undefined,
        };
        deferred.resolve(canContinue);
    }

    /** 清理自动进入下一阶段的计时器。 */
    private _clearAutoAdvanceTimer(): void {
        if (this._autoAdvanceTimerId === undefined) return;
        this._runtime.clearTimeout(this._autoAdvanceTimerId);
        this._autoAdvanceTimerId = undefined;
    }

    /** 清理 Welcome 自动结束计时器。 */
    private _clearWelcomeTimer(): void {
        if (this._welcomeTimerId === undefined) return;
        this._runtime.clearTimeout(this._welcomeTimerId);
        this._welcomeTimerId = undefined;
    }

    /** 等待 Welcome 退场后的两秒缓冲，再允许 Step 1 激活。 */
    private _waitForStepOneTransition(): Promise<void> {
        return new Promise<void>((resolve) => {
            this._resolveStepOneTransition = resolve;
            this._stepOneTransitionTimerId = this._runtime.setTimeout(() => {
                this._stepOneTransitionTimerId = undefined;
                this._resolveStepOneTransition = undefined;
                resolve();
            }, STEP_ONE_TRANSITION_DELAY_MS);
        });
    }

    /** 清理 Welcome 与 Step 1 之间的过渡计时器并结束待决等待。 */
    private _clearStepOneTransitionTimer(): void {
        if (this._stepOneTransitionTimerId !== undefined) {
            this._runtime.clearTimeout(this._stepOneTransitionTimerId);
            this._stepOneTransitionTimerId = undefined;
        }
        const resolve = this._resolveStepOneTransition;
        this._resolveStepOneTransition = undefined;
        resolve?.();
    }

    /** 幂等卸载当前 Welcome 弹窗。 */
    private _disposeWelcome(): void {
        this._clearWelcomeTimer();
        const handle = this._welcomeHandle;
        this._welcomeHandle = undefined;
        handle?.dispose();
    }

    /** Welcome 或 Step 1 首次成功激活时记录账号级入口状态。 */
    private _markEntrySeen(): void {
        if (this._hasMarkedEntrySeen) return;
        this._hasMarkedEntrySeen = true;
        this._onboardingEligibilityService.markEntryOnboardingSeen();
    }

    /** 判断异步回调是否仍属于当前页面生命周期。 */
    private _isCurrent(lifecycleId: number): boolean {
        return lifecycleId === this._lifecycleId;
    }

    /** 上报首次 Guide 事件；埋点失败不影响引导和业务操作。 */
    private _track(eventCode: string, detail: Record<string, string> = {}): void {
        void this._ravenService.track(eventCode, {
            eventType: TRACK_EVENT_TYPE.SS_PLG_PAGE_VIEW,
            sourceType: eventCode,
            eventDetailStr: detail,
        }).catch(() => undefined);
    }
}
